import { motion } from "framer-motion";
import {
	CheckCircle2,
	ChevronDown,
	ChevronUp,
	Clock,
	Columns3,
	DollarSign,
	Filter,
	LayoutGrid,
	Plus,
	Rocket,
	Search,
	Target,
	TrendingUp,
	Zap,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EmptyWalletIcon } from "../components/icons/EmptyWalletIcon";
import { KanbanBoard } from "../components/kanban/KanbanBoard";
import LoadingSpinner from "../components/LoadingSpinner";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { useWallets } from "../context/WalletContext";
import { ASSETS, CHAINS, INTEGRATION_STATUSES } from "../lib/constants";
import {
	getIntegrationBadgeVariant,
	getOnboardingBadgeVariant,
} from "../lib/statusBadge";
import { cn } from "../lib/utils";
import { authService } from "../services/auth/authService";
import type { IntegrationStatus, User } from "../types";

function formatCurrency(amount: number): string {
	if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(2)}M`;
	if (amount >= 1_000) return `$${Math.round(amount / 1_000)}K`;
	return `$${amount}`;
}

const TIER_OPTIONS = [
	{ value: "All", label: "All Tiers" },
	{ value: "1", label: "Tier 1" },
	{ value: "2", label: "Tier 2" },
	{ value: "3", label: "Tier 3" },
] as const;

const Dashboard: React.FC = () => {
	const navigate = useNavigate();
	const { isAdmin } = useAuth();
	const {
		wallets,
		loading: walletsLoading,
		error: walletsError,
		updateWallet,
	} = useWallets();
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<IntegrationStatus | "All">(
		"All",
	);
	const [expandedWallet, setExpandedWallet] = useState<string | null>(null);
	const [chainFilter, setChainFilter] = useState<string>("All");
	const [assetFilter, setAssetFilter] = useState<string>("All");
	const [tierFilter, setTierFilter] = useState<string>("All");
	const [allUsers, setAllUsers] = useState<User[]>([]);
	const [viewMode, setViewMode] = useState<"grid" | "kanban">(() => {
		return (
			(localStorage.getItem("dashboard-view-mode") as
				| "grid"
				| "kanban"
				| null) ?? "grid"
		);
	});

	useEffect(() => {
		localStorage.setItem("dashboard-view-mode", viewMode);
	}, [viewMode]);

	useEffect(() => {
		const loadUsers = async () => {
			try {
				const users = await authService.getAllUsers();
				setAllUsers(users);
			} catch (error) {
				console.error("Error loading users:", error);
			}
		};
		loadUsers();
	}, []);

	const getInitials = (name: string) => {
		const parts = name.split(" ");
		if (parts.length >= 2) {
			return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
		}
		return name.substring(0, 2).toUpperCase();
	};

	const walletUsersMap = useMemo(() => {
		const map: Record<string, User[]> = {};
		for (const wallet of wallets) {
			map[wallet.id] = allUsers.filter((user) =>
				user.wallet_ids?.includes(wallet.id),
			);
		}
		return map;
	}, [wallets, allUsers]);

	const getWalletUsers = useCallback(
		(walletId: string) => walletUsersMap[walletId] ?? [],
		[walletUsersMap],
	);

	const handleStatCardClick = (
		filter: IntegrationStatus | "All" | "InProgress",
	) => {
		if (filter === "InProgress") {
			setStatusFilter("All");
		} else {
			setStatusFilter(filter);
		}
	};

	// Partner tracker aggregate stats
	const partnerStats = useMemo(() => {
		let totalGrantFunding = 0;
		let activeGrants = 0;
		let taskProgressSum = 0;
		let taskProgressCount = 0;

		for (const w of wallets) {
			if (w.marketing_grant?.grant_status === "Active") {
				activeGrants++;
				if (w.marketing_grant.total_grant_usd != null) {
					totalGrantFunding += w.marketing_grant.total_grant_usd;
				}
			}
			if (w.customer_success?.tasks_progress != null) {
				taskProgressSum += w.customer_success.tasks_progress;
				taskProgressCount++;
			}
		}

		return {
			totalGrantFunding,
			activeGrants,
			avgTaskProgress:
				taskProgressCount > 0
					? Math.round(taskProgressSum / taskProgressCount)
					: null,
		};
	}, [wallets]);

	const filteredWallets = useMemo(
		() =>
			wallets.filter((wallet) => {
				const matchesSearch =
					(wallet.wallet_name ?? "")
						.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					(wallet.company_name ?? "")
						.toLowerCase()
						.includes(searchTerm.toLowerCase());

				const matchesStatus =
					statusFilter === "All" || wallet.integration_status === statusFilter;

				const matchesChain =
					chainFilter === "All" || wallet.chain === chainFilter;
				const matchesAsset =
					assetFilter === "All" || wallet.asset === assetFilter;

				const matchesTier =
					tierFilter === "All" ||
					wallet.customer_success?.tier === Number(tierFilter);

				return (
					matchesSearch &&
					matchesStatus &&
					matchesChain &&
					matchesAsset &&
					matchesTier
				);
			}),
		[wallets, searchTerm, statusFilter, chainFilter, assetFilter, tierFilter],
	);

	const toggleExpanded = (walletId: string, e: React.MouseEvent) => {
		e.stopPropagation();
		setExpandedWallet(expandedWallet === walletId ? null : walletId);
	};

	const filterSelectClass =
		"h-11 appearance-none rounded-xl border border-(--color-border) bg-(--color-muted) px-4 text-sm text-(--color-foreground) shadow-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent-border) cursor-pointer min-w-[140px]";

	if (walletsLoading) {
		return <LoadingSpinner size="lg" message="Loading dashboard..." centered />;
	}

	return (
		<div className="min-h-[calc(100vh-56px)] overflow-x-hidden bg-(--color-background) px-4 pb-8 pt-20">
			{walletsError && (
				<Alert variant="destructive" className="m-4">
					<AlertDescription>
						<strong>Unable to load wallets:</strong> {walletsError}
					</AlertDescription>
				</Alert>
			)}
			<div className="mx-auto max-w-[1400px]">
				{/* Header with stats */}
				<div className="my-6">
					<div className="mb-5 flex flex-wrap items-end justify-between gap-4">
						<div>
							<h1 className="text-3xl md:text-4xl font-bold text-(--color-foreground) mb-2 normal-case">
								Wallet Partners
							</h1>
							<p className="text-(--color-muted-foreground) text-base">
								Manage and monitor your integration partners
							</p>
						</div>
						<div className="flex items-center gap-3">
							{/* View toggle */}
							<div className="flex items-center rounded-xl border border-(--color-border) p-0.5">
								<Button
									variant={viewMode === "grid" ? "subtle" : "ghost"}
									size="icon"
									className="h-7 w-7"
									onClick={() => setViewMode("grid")}
									title="Grid view"
								>
									<LayoutGrid size={15} />
								</Button>
								<Button
									variant={viewMode === "kanban" ? "subtle" : "ghost"}
									size="icon"
									className="h-7 w-7"
									onClick={() => setViewMode("kanban")}
									title="Board view"
								>
									<Columns3 size={15} />
								</Button>
							</div>
							{isAdmin && (
								<Button
									onClick={() => navigate("/add-wallet")}
									size="lg"
									className="rounded-xl bg-(--color-cta-primary) hover:bg-(--color-cta-primary-hover) text-(--color-cta-primary-foreground) shadow-lg hover:shadow-xl transition-all"
								>
									<Plus size={20} />
									Add Wallet
								</Button>
							)}
						</div>
					</div>

					{/* Stat cards */}
					<div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5 mb-5">
						{[
							{
								value: wallets.length,
								label: "Total Wallets",
								icon: Rocket,
								filter: "All" as const,
								color: "text-(--color-accent)",
								bg: "bg-(--color-accent-muted)",
								border: "border-(--color-accent-border)",
							},
							{
								value: wallets.filter((w) => w.integration_status === "Live")
									.length,
								label: "Live",
								icon: CheckCircle2,
								filter: "Live" as IntegrationStatus,
								color: "text-[#00E6C3]",
								bg: "bg-[#00E6C3]/12",
								border: "border-[#00E6C3]/35",
							},
							{
								value: wallets.filter(
									(w) =>
										w.integration_status === "Integrating" ||
										w.integration_status === "Testing",
								).length,
								label: "In Progress",
								icon: Zap,
								filter: "Integrating" as IntegrationStatus,
								color: "text-[#F59E0B]",
								bg: "bg-[#F59E0B]/12",
								border: "border-[#F59E0B]/35",
							},
							{
								value: wallets.filter(
									(w) => w.integration_status === "New - Pre-Integration",
								).length,
								label: "New",
								icon: Clock,
								filter: "New - Pre-Integration" as IntegrationStatus,
								color: "text-(--color-muted-foreground)",
								bg: "bg-(--color-muted)",
								border: "border-(--color-border)",
							},
						].map(
							({ value, label, icon: Icon, filter, color, bg, border }, i) => (
								<motion.div
									key={label}
									initial={{ opacity: 0, y: 24 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: i * 0.1, duration: 0.5 }}
								>
									<button
										type="button"
										className={cn(
											"w-full rounded-xl border p-3 text-left transition-all duration-200",
											statusFilter === filter
												? `${bg} ${border} shadow-lg scale-[1.02]`
												: `bg-(--color-card) border-(--color-border) hover:${bg} hover:${border}`,
										)}
										onClick={() => handleStatCardClick(filter)}
									>
										<div className="flex items-center justify-between mb-1.5">
											<div
												className={cn("p-1 rounded-lg", bg, border, "border")}
											>
												<Icon className={cn("w-3.5 h-3.5", color)} />
											</div>
										</div>
										<p className="text-xl font-bold text-(--color-foreground) mb-0.5">
											{value}
										</p>
										<p className="text-[9px] font-medium text-(--color-muted-foreground) uppercase tracking-wider">
											{label}
										</p>
									</button>
								</motion.div>
							),
						)}
						{/* Partner tracker stat cards */}
						{partnerStats.totalGrantFunding > 0 && (
							<motion.div
								initial={{ opacity: 0, y: 24 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.4, duration: 0.5 }}
							>
								<div className="w-full rounded-xl border p-3 text-left bg-(--color-card) border-(--color-border)">
									<div className="flex items-center justify-between mb-1.5">
										<div className="p-1 rounded-lg bg-[#00C49A]/12 border border-[#00C49A]/35">
											<DollarSign className="w-3.5 h-3.5 text-[#00C49A]" />
										</div>
									</div>
									<p className="text-xl font-bold text-(--color-foreground) mb-0.5">
										{formatCurrency(partnerStats.totalGrantFunding)}
									</p>
									<p className="text-[9px] font-medium text-(--color-muted-foreground) uppercase tracking-wider">
										Active Grant $
									</p>
								</div>
							</motion.div>
						)}
						{partnerStats.activeGrants > 0 && (
							<motion.div
								initial={{ opacity: 0, y: 24 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.5, duration: 0.5 }}
							>
								<div className="w-full rounded-xl border p-3 text-left bg-(--color-card) border-(--color-border)">
									<div className="flex items-center justify-between mb-1.5">
										<div className="p-1 rounded-lg bg-[#3B82F6]/12 border border-[#3B82F6]/35">
											<TrendingUp className="w-3.5 h-3.5 text-[#3B82F6]" />
										</div>
									</div>
									<p className="text-xl font-bold text-(--color-foreground) mb-0.5">
										{partnerStats.activeGrants}
									</p>
									<p className="text-[9px] font-medium text-(--color-muted-foreground) uppercase tracking-wider">
										Active Grants
									</p>
								</div>
							</motion.div>
						)}
						{partnerStats.avgTaskProgress != null && (
							<motion.div
								initial={{ opacity: 0, y: 24 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.6, duration: 0.5 }}
							>
								<div className="w-full rounded-xl border p-3 text-left bg-(--color-card) border-(--color-border)">
									<div className="flex items-center justify-between mb-1.5">
										<div className="p-1 rounded-lg bg-[#F59E0B]/12 border border-[#F59E0B]/35">
											<Target className="w-3.5 h-3.5 text-[#F59E0B]" />
										</div>
									</div>
									<p className="text-xl font-bold text-(--color-foreground) mb-0.5">
										{partnerStats.avgTaskProgress}%
									</p>
									<p className="text-[9px] font-medium text-(--color-muted-foreground) uppercase tracking-wider">
										Avg Progress
									</p>
								</div>
							</motion.div>
						)}
					</div>
				</div>

				{/* Filters */}
				<Card className="mb-6 p-5 bg-(--color-card) border-(--color-border) rounded-2xl">
					<div className="flex flex-wrap items-center gap-4">
						<div className="relative min-w-[280px] flex-1">
							<Search
								size={18}
								className="absolute top-1/2 left-4 -translate-y-1/2 text-(--color-muted-foreground)"
							/>
							<Input
								type="text"
								placeholder="Search by wallet name or company..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-11 h-11 bg-(--color-muted) border-(--color-border) focus:ring-2 focus:ring-(--color-accent-border) focus:border-(--color-accent)"
							/>
						</div>

						<div className="flex items-center gap-3 text-(--color-muted-foreground)">
							<Filter size={18} />
							<select
								value={statusFilter}
								onChange={(e) =>
									setStatusFilter(e.target.value as IntegrationStatus | "All")
								}
								className={cn(filterSelectClass, "min-w-[160px]")}
							>
								<option value="All">All Statuses</option>
								{INTEGRATION_STATUSES.map((s) => (
									<option key={s} value={s}>
										{s}
									</option>
								))}
							</select>
						</div>

						<select
							value={chainFilter}
							onChange={(e) => setChainFilter(e.target.value)}
							className={filterSelectClass}
						>
							<option value="All">All Chains</option>
							{CHAINS.map((c) => (
								<option key={c} value={c}>
									{c}
								</option>
							))}
						</select>

						<select
							value={assetFilter}
							onChange={(e) => setAssetFilter(e.target.value)}
							className={filterSelectClass}
						>
							<option value="All">All Assets</option>
							{ASSETS.map((a) => (
								<option key={a} value={a}>
									{a}
								</option>
							))}
						</select>

						<div className="flex items-center gap-2 text-(--color-muted-foreground)">
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
							</svg>
							<select
								value={tierFilter}
								onChange={(e) => setTierFilter(e.target.value)}
								className={filterSelectClass}
							>
								{TIER_OPTIONS.map((t) => (
									<option key={t.value} value={t.value}>
										{t.label}
									</option>
								))}
							</select>
						</div>
					</div>
				</Card>

				{/* Wallet grid, kanban, or empty state */}
				{filteredWallets.length === 0 ? (
					<motion.div
						initial={{ opacity: 0, y: 24 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
					>
						<Card className="flex flex-col items-center p-12 text-center bg-(--color-card) border-(--color-border) rounded-2xl">
							<EmptyWalletIcon size={140} />
							<h3 className="mt-6 mb-3 text-(--color-foreground) text-xl font-semibold">
								No Wallets Found
							</h3>
							<p className="mb-6 text-(--color-muted-foreground) text-sm max-w-md">
								{wallets.length === 0
									? "Get started by adding your first wallet partner to begin the integration journey"
									: "Try adjusting your search or filters to find what you're looking for"}
							</p>
							{wallets.length === 0 && (
								<Button
									onClick={() => navigate("/add-wallet")}
									className="bg-(--color-cta-primary) hover:bg-(--color-cta-primary-hover) text-(--color-cta-primary-foreground) shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
								>
									<Plus size={20} />
									Add Your First Wallet
								</Button>
							)}
						</Card>
					</motion.div>
				) : viewMode === "kanban" ? (
					<KanbanBoard
						wallets={filteredWallets}
						onMove={
							isAdmin
								? (walletId, newStatus) =>
										updateWallet(walletId, { integration_status: newStatus })
								: undefined
						}
					/>
				) : (
					<div className="grid gap-5 [grid-template-columns:repeat(auto-fill,minmax(380px,1fr))]">
						{filteredWallets.map((wallet, i) => (
							<motion.div
								key={wallet.id}
								initial={{ opacity: 0, y: 24 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: i * 0.05, duration: 0.5 }}
								className="h-full"
							>
								<Card className="flex h-full cursor-default flex-col p-6 transition-all duration-200 bg-(--color-card) border-(--color-border) rounded-2xl hover:border-(--color-accent) hover:shadow-lg hover:scale-[1.01]">
									{/* Card header - clickable */}
									{/* biome-ignore lint/a11y/useSemanticElements: Card header needs to be a div for styling */}
									<div
										className="mb-4 flex cursor-pointer items-start justify-between border-(--color-border) border-b pb-4"
										role="button"
										tabIndex={0}
										onClick={() => navigate(`/wallet/${wallet.id}`)}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												navigate(`/wallet/${wallet.id}`);
											}
										}}
									>
										<div>
											<h3 className="mb-1 font-semibold text-(--color-foreground) text-lg tracking-tight">
												{wallet.wallet_name}
											</h3>
											<p className="text-(--color-muted-foreground) text-sm">
												{wallet.company_name}
											</p>
											{getWalletUsers(wallet.id).length > 0 && (
												<div className="mt-2 flex -space-x-2">
													{getWalletUsers(wallet.id)
														.slice(0, 3)
														.map((user) => (
															<Avatar
																key={user.id}
																title={user.name}
																className="h-7 w-7 border-white dark:border-[#0a1628] border-2 text-xs ring-1 ring-[#E5E5E5] dark:ring-white/10"
															>
																<AvatarFallback className="text-[0.625rem] bg-(--color-muted) text-(--color-foreground)">
																	{getInitials(user.name)}
																</AvatarFallback>
															</Avatar>
														))}
													{getWalletUsers(wallet.id).length > 3 && (
														<Avatar className="h-7 w-7 border-white dark:border-[#0a1628] border-2 text-xs ring-1 ring-[#E5E5E5] dark:ring-white/10">
															<AvatarFallback className="text-[0.625rem] bg-(--color-muted) text-(--color-foreground)">
																+{getWalletUsers(wallet.id).length - 3}
															</AvatarFallback>
														</Avatar>
													)}
												</div>
											)}
										</div>
										<div className="flex shrink-0 flex-col items-end gap-2">
											<Badge
												variant={getIntegrationBadgeVariant(
													wallet.integration_status,
												)}
												className="text-xs"
											>
												{wallet.integration_status}
											</Badge>
											<Badge
												variant={getOnboardingBadgeVariant(
													wallet.onboarding_status,
												)}
												className="text-[0.688rem]"
											>
												{wallet.onboarding_status}
											</Badge>
										</div>
									</div>

									{/* Detail rows - clickable */}
									{/* biome-ignore lint/a11y/useSemanticElements: Card details needs to be a div for styling */}
									<div
										className="mb-4 flex-1 cursor-pointer transition-opacity hover:opacity-80"
										role="button"
										tabIndex={0}
										onClick={() => navigate(`/wallet/${wallet.id}`)}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												navigate(`/wallet/${wallet.id}`);
											}
										}}
									>
										{[
											{ label: "Chain", value: wallet.chain },
											{ label: "Asset", value: wallet.asset },
											{
												label: "Products",
												value: (wallet.products ?? []).join(", "),
											},
											{ label: "Type", value: wallet.custodial_type },
											...(wallet.customer_success?.kam
												? [
														{
															label: "KAM",
															value: wallet.customer_success.kam,
														},
													]
												: []),
											...(wallet.customer_success?.tier
												? [
														{
															label: "Tier",
															value: `Tier ${wallet.customer_success.tier}`,
														},
													]
												: []),
										].map(({ label, value }) => (
											<div
												key={label}
												className="flex items-center justify-between py-2"
											>
												<span className="font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wide">
													{label}:
												</span>
												<span className="font-medium text-(--color-foreground) text-sm">
													{value}
												</span>
											</div>
										))}
									</div>

									{/* Expanded section */}
									{expandedWallet === wallet.id && (
										<motion.div
											initial={{ opacity: 0, height: 0 }}
											animate={{ opacity: 1, height: "auto" }}
											exit={{ opacity: 0, height: 0 }}
											transition={{ duration: 0.3 }}
											className="mb-4 rounded-xl border border-(--color-border) bg-(--color-muted) p-4"
										>
											{/* Contact info */}
											<div className="mb-3.5">
												<h4 className="mb-2 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wide">
													Contact Information
												</h4>
												{wallet.main_contact_name && (
													<p className="my-1 text-(--color-foreground) text-sm">
														<strong>Name:</strong> {wallet.main_contact_name}
													</p>
												)}
												{wallet.main_contact_email && (
													<p className="my-1 text-(--color-foreground) text-sm">
														<strong>Email:</strong> {wallet.main_contact_email}
													</p>
												)}
												{wallet.main_contact_phone && (
													<p className="my-1 text-(--color-foreground) text-sm">
														<strong>Phone:</strong> {wallet.main_contact_phone}
													</p>
												)}
												{wallet.main_contact_role && (
													<p className="my-1 text-(--color-foreground) text-sm">
														<strong>Role:</strong> {wallet.main_contact_role}
													</p>
												)}
											</div>

											{(wallet.slack_channel ||
												wallet.home_domain ||
												wallet.funding_address) && (
												<div className="mb-3.5">
													<h4 className="mb-2 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wide">
														Technical Information
													</h4>
													{wallet.slack_channel && (
														<p className="my-1 text-(--color-foreground) text-sm">
															<strong>Slack:</strong> {wallet.slack_channel}
														</p>
													)}
													{wallet.home_domain && (
														<p className="my-1 text-(--color-foreground) text-sm">
															<strong>Home Domain:</strong> {wallet.home_domain}
														</p>
													)}
													{wallet.funding_address && (
														<p className="my-1 text-(--color-foreground) text-sm">
															<strong>Funding Address:</strong>{" "}
															{wallet.funding_address}
														</p>
													)}
												</div>
											)}

											{wallet.is_whitelisted &&
												wallet.whitelist_environments &&
												wallet.whitelist_environments.length > 0 && (
													<div className="mb-3.5">
														<h4 className="mb-2 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wide">
															Whitelisted Environments
														</h4>
														<div className="flex flex-wrap gap-1.5">
															{wallet.whitelist_environments.map((env) => (
																<Badge key={env} variant="info">
																	{env}
																</Badge>
															))}
														</div>
													</div>
												)}

											{wallet.priority_markets &&
												wallet.priority_markets.length > 0 && (
													<div className="mb-3.5">
														<h4 className="mb-2 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wide">
															Priority Markets
														</h4>
														<p className="text-(--color-foreground) text-sm">
															{wallet.priority_markets.slice(0, 5).join(", ")}
															{wallet.priority_markets.length > 5 &&
																` +${wallet.priority_markets.length - 5} more`}
														</p>
													</div>
												)}

											{(wallet.conducts_kyc !== undefined ||
												wallet.mau ||
												wallet.country_of_incorporation) && (
												<div>
													<h4 className="mb-2 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wide">
														Additional Details
													</h4>
													{wallet.conducts_kyc !== undefined && (
														<p className="my-1 text-(--color-foreground) text-sm">
															<strong>Conducts KYC:</strong>{" "}
															{wallet.conducts_kyc ? "Yes" : "No"}
														</p>
													)}
													{wallet.mau && (
														<p className="my-1 text-(--color-foreground) text-sm">
															<strong>MAU:</strong> {wallet.mau}
														</p>
													)}
													{wallet.country_of_incorporation && (
														<p className="my-1 text-(--color-foreground) text-sm">
															<strong>Country:</strong>{" "}
															{wallet.country_of_incorporation}
														</p>
													)}
												</div>
											)}
										</motion.div>
									)}

									{/* Footer */}
									<div className="mt-auto flex items-center justify-between border-(--color-border) border-t pt-4">
										<button
											type="button"
											className="flex items-center gap-2 rounded-xl border border-(--color-accent) px-4 py-2 font-medium text-(--color-accent) text-sm transition-all duration-200 hover:bg-(--color-accent) hover:text-white active:scale-[0.98]"
											onClick={(e) => toggleExpanded(wallet.id, e)}
										>
											{expandedWallet === wallet.id ? (
												<>
													<ChevronUp size={16} />
													<span>Hide Details</span>
												</>
											) : (
												<>
													<ChevronDown size={16} />
													<span>Show Details</span>
												</>
											)}
										</button>
										<div className="text-(--color-muted-foreground) text-xs">
											Updated {new Date(wallet.updated_at).toLocaleDateString()}
										</div>
									</div>
								</Card>
							</motion.div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default Dashboard;
